import React, { useState, useEffect, useRef } from "react";
import api from "../api";

export default function LinkedInConnect() {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState("");
  const pollingRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const data = await api.get("/api/linkedin/status");
      setStatus(data);
      window.dispatchEvent(
        new CustomEvent("linkedin-status", { detail: data })
      );
      if (data.detail && !data.connected) {
        setError(data.detail);
      }
      if (data.connected === true && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setConnecting(false);
        setError("");
      }
    } catch {
      setStatus({ connected: false });
      window.dispatchEvent(
        new CustomEvent("linkedin-status", { detail: { connected: false } })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const lockedName =
    status.locked_identity?.display_name ||
    status.locked_identity?.public_id ||
    status.account_name ||
    "";

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError("");
      const data = await api.post("/api/linkedin/connect");
      if (data.must_reconnect_same && data.locked_identity) {
        const name =
          data.locked_identity.display_name || data.locked_identity.public_id;
        if (
          !window.confirm(
            `Vous devez vous reconnecter avec le même profil LinkedIn : « ${name} ».\n\nTout autre compte sera refusé.`
          )
        ) {
          setConnecting(false);
          return;
        }
      }
      if (data.url) {
        window.open(data.url, "_blank", "width=600,height=700");
        pollingRef.current = setInterval(fetchStatus, 10000);
      }
    } catch (e) {
      setConnecting(false);
      setError(e?.message || e?.detail || "Connexion impossible");
    }
  };

  const handleDisconnect = async () => {
    const name = lockedName || "ce profil";
    if (
      !window.confirm(
        `Déconnecter LinkedIn ?\n\nImportant : vous ne pourrez ensuite reconnecter QUE « ${name} ». Changer de compte LinkedIn est interdit.`
      )
    ) {
      return;
    }
    try {
      setDisconnecting(true);
      setError("");
      const data = await api.post("/api/linkedin/disconnect");
      const next = {
        connected: false,
        locked_identity: data.locked_identity || status.locked_identity,
      };
      setStatus(next);
      window.dispatchEvent(
        new CustomEvent("linkedin-status", { detail: next })
      );
    } catch {
      /* ignore */
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-ink/5 rounded-lg" />
          <div className="h-4 bg-ink/5 rounded w-40" />
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              status.connected ? "bg-[#0A66C2]/10" : "bg-ink/5"
            }`}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path
                className={status.connected ? "text-[#0A66C2]" : "text-ink/30"}
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-ink font-display tracking-tight">
              Connexion LinkedIn
            </h3>
            {status.connected ? (
              <div className="mt-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-sm font-medium text-emerald-700">
                    Compte connecté
                  </span>
                </div>
                {status.account_name && (
                  <p className="text-sm text-ink/50 mt-0.5">
                    {status.account_name}
                    {status.public_id ? (
                      <span className="text-ink/35"> · {status.public_id}</span>
                    ) : null}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-0.5">
                <p className="text-sm text-mute">
                  {lockedName
                    ? `Reconnectez uniquement « ${lockedName} »`
                    : "Connectez votre compte pour activer l'envoi automatique"}
                </p>
              </div>
            )}
          </div>
        </div>

        {status.connected ? (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-full">
              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-semibold text-emerald-700">
                LinkedIn connecté
              </span>
            </div>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="text-sm text-mute hover:text-rouge underline underline-offset-2 disabled:opacity-50"
            >
              {disconnecting ? "Déconnexion…" : "Déconnecter"}
            </button>
          </div>
        ) : (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="bg-[#0A66C2] hover:bg-[#004182] text-white font-semibold py-2.5 px-5 rounded-md transition-colors duration-150 text-sm flex items-center gap-2"
          >
            {connecting ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Connexion en cours...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                {lockedName ? "Reconnecter mon LinkedIn" : "Connecter LinkedIn"}
              </>
            )}
          </button>
        )}
      </div>
      {error ? (
        <p className="mt-3 text-sm text-rouge">{error}</p>
      ) : null}
    </div>
  );
}
